<template>
    <ContentModal
        :modelValue="modelValue"
        title="Auto-Engine Insights · Superadmin"
        size="2xl"
        @update:modelValue="emit('update:modelValue', $event)"
    >
        <div class="engine-insights">
            <header class="insights-hero">
                <div class="insights-kicker"><i class="material-icons">query_stats</i> PLATFORM PULSE</div>
                <div class="insights-heading">
                    <div>
                        <h2>Auto-Engine Insights</h2>
                        <p>See where automated scheduling is completing work and how long each run takes.</p>
                    </div>
                    <span class="scope-pill">All organizations</span>
                </div>
            </header>

            <div class="range-controls">
                <label>
                    <span>From</span>
                    <input v-model="fromDate" type="date" class="form-control form-control-sm">
                </label>
                <label>
                    <span>Through</span>
                    <input v-model="toDate" type="date" class="form-control form-control-sm">
                </label>
                <button type="button" class="btn btn-primary btn-sm mb-0" :disabled="loading || !rangeValid" @click="load">
                    <i class="material-icons align-middle me-1" style="font-size:16px;">refresh</i>
                    {{ loading ? "Refreshing..." : "Refresh insights" }}
                </button>
                <span v-if="stats?.generated_at" class="updated-label">
                    Updated {{ formatTimestamp(stats.generated_at) }}
                </span>
            </div>
            <p v-if="rangeError" class="alert alert-warning py-2 mb-3">{{ rangeError }}</p>

            <div v-if="loading && !stats" class="loading-state" role="status">
                <span class="spinner-border text-primary" aria-hidden="true"></span>
                <span>Gathering organization and location activity...</span>
            </div>
            <div v-else-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center gap-3" role="alert">
                <span>{{ errorMessage }}</span>
                <button type="button" class="btn btn-sm btn-outline-danger mb-0" :disabled="loading" @click="load">
                    Try again
                </button>
            </div>
            <template v-else-if="stats">
                <section class="metric-grid" aria-label="Auto-engine summary">
                    <article class="metric metric--green">
                        <span class="metric-label">Completed runs</span>
                        <strong>{{ formatNumber(summary.completed_runs) }}</strong>
                        <small>Within the selected period</small>
                    </article>
                    <article class="metric metric--blue">
                        <span class="metric-label">Organizations served</span>
                        <strong>{{ formatNumber(summary.organizations_with_completions) }}</strong>
                        <small>{{ formatNumber(summary.locations_with_completions) }} locations completed work</small>
                    </article>
                    <article class="metric metric--violet">
                        <span class="metric-label">Average run time</span>
                        <strong>{{ formatDuration(summary.average_duration_seconds) }}</strong>
                        <small>Completed executions only</small>
                    </article>
                    <article class="metric metric--amber">
                        <span class="metric-label">Currently in progress</span>
                        <strong>{{ formatNumber(summary.in_progress_runs) }}</strong>
                        <small>Runs without a recorded finish time</small>
                    </article>
                </section>

                <div class="visualization-grid">
                    <section class="insight-panel">
                        <div class="panel-heading">
                            <div>
                                <h3>Completion rhythm</h3>
                                <p>Completed executions by start date</p>
                            </div>
                            <span class="panel-icon panel-icon--blue"><i class="material-icons">bar_chart</i></span>
                        </div>
                        <div v-if="dailyBars.length" class="daily-chart" role="img" aria-label="Completed auto-engine runs by day">
                            <div v-for="bar in dailyBars" :key="bar.date" class="daily-column" :title="`${bar.dateLabel}: ${bar.completed_runs} completed`">
                                <span class="daily-value">{{ bar.completed_runs || "" }}</span>
                                <div class="daily-track">
                                    <div class="daily-bar" :style="{ height: `${bar.height}%` }"></div>
                                </div>
                                <small>{{ bar.showLabel ? bar.dateLabel : "" }}</small>
                            </div>
                        </div>
                        <p v-else class="empty-note">No completed runs in this date range.</p>
                    </section>

                    <section class="insight-panel">
                        <div class="panel-heading">
                            <div>
                                <h3>Work by engine type</h3>
                                <p>Completed runs and average duration</p>
                            </div>
                            <span class="panel-icon panel-icon--green"><i class="material-icons">build_circle</i></span>
                        </div>
                        <div v-if="stats.by_type?.length" class="type-breakdown">
                            <div v-for="type in stats.by_type" :key="type.execution_type" class="type-row">
                                <div class="type-copy">
                                    <strong>{{ formatExecutionType(type.execution_type) }}</strong>
                                    <span>{{ formatNumber(type.completed_runs) }} / {{ formatNumber(type.total_runs) }} completed</span>
                                </div>
                                <div class="type-track">
                                    <div
                                        class="type-bar"
                                        :style="{ width: `${completionPercent(type)}%` }"
                                    ></div>
                                </div>
                                <small>{{ formatDuration(type.average_duration_seconds) }} avg</small>
                            </div>
                        </div>
                        <p v-else class="empty-note">No engine activity to break down yet.</p>
                    </section>
                </div>

                <section class="insight-panel data-panel">
                    <div class="panel-heading">
                        <div>
                            <h3>Organization coverage</h3>
                            <p>Where the engine completed work during this period</p>
                        </div>
                        <span class="result-count">{{ stats.organizations?.length || 0 }} organizations</span>
                    </div>
                    <div v-if="stats.organizations?.length" class="table-responsive insights-table-wrap">
                        <table class="table align-middle insights-table mb-0">
                            <thead>
                                <tr>
                                    <th>Organization</th>
                                    <th>Locations</th>
                                    <th>Completed</th>
                                    <th>In progress</th>
                                    <th>Avg. duration</th>
                                    <th>Total run time</th>
                                    <th>Last completion</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="organization in stats.organizations" :key="organization.organization_id || organization.organization_name">
                                    <td class="entity-name">{{ organization.organization_name }}</td>
                                    <td>{{ formatNumber(organization.locations_with_completions) }} / {{ formatNumber(organization.location_count) }}</td>
                                    <td><span class="completion-count">{{ formatNumber(organization.completed_runs) }}</span></td>
                                    <td>{{ formatNumber(organization.in_progress_runs) }}</td>
                                    <td>{{ formatDuration(organization.average_duration_seconds) }}</td>
                                    <td>{{ formatDuration(organization.total_duration_seconds) }}</td>
                                    <td>{{ formatTimestamp(organization.last_completed_at) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p v-else class="empty-note">No organizations have auto-engine activity in this date range.</p>
                </section>

                <section class="insight-panel data-panel">
                    <div class="panel-heading location-heading">
                        <div>
                            <h3>Location execution details</h3>
                            <p>Search every location with recorded engine activity</p>
                        </div>
                        <input
                            v-model.trim="locationSearch"
                            type="search"
                            class="form-control form-control-sm location-search"
                            placeholder="Search organization or location"
                            aria-label="Search locations"
                        >
                    </div>
                    <div v-if="filteredLocations.length" class="table-responsive insights-table-wrap location-table-wrap">
                        <table class="table align-middle insights-table mb-0">
                            <thead>
                                <tr>
                                    <th>Organization / location</th>
                                    <th>Completed</th>
                                    <th>In progress</th>
                                    <th>Avg. duration</th>
                                    <th>Total run time</th>
                                    <th>Last completion</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="location in filteredLocations" :key="location.location_id">
                                    <td>
                                        <strong class="entity-name">{{ location.location_name }}</strong>
                                        <small class="d-block text-muted">{{ location.organization_name }}</small>
                                    </td>
                                    <td><span class="completion-count">{{ formatNumber(location.completed_runs) }}</span></td>
                                    <td>{{ formatNumber(location.in_progress_runs) }}</td>
                                    <td>{{ formatDuration(location.average_duration_seconds) }}</td>
                                    <td>{{ formatDuration(location.total_duration_seconds) }}</td>
                                    <td>{{ formatTimestamp(location.last_completed_at) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p v-else class="empty-note">
                        {{ locationSearch ? "No locations match your search." : "No locations have auto-engine activity in this date range." }}
                    </p>
                </section>

                <p class="data-note mb-0">
                    Completion means the tracker has a finish timestamp. Durations use recorded start and finish times; active runs are counted separately.
                </p>
            </template>
        </div>
    </ContentModal>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import api from "@/config/api";
import ContentModal from "@/components/Modal/ContentModal.vue";

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(["update:modelValue"]);

function toDateInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

const today = new Date();
const start = new Date(today);
start.setDate(start.getDate() - 29);
const fromDate = ref(toDateInputValue(start));
const toDate = ref(toDateInputValue(today));
const stats = ref(null);
const loading = ref(false);
const errorMessage = ref("");
const locationSearch = ref("");

const summary = computed(() => stats.value?.summary || {});
const rangeError = computed(() => {
    if (!fromDate.value || !toDate.value) return "Choose both dates to load insights.";
    if (fromDate.value > toDate.value) return "The start date must be on or before the end date.";
    const days = (Date.parse(`${toDate.value}T00:00:00Z`) - Date.parse(`${fromDate.value}T00:00:00Z`)) / 86400000;
    return days > 365 ? "Choose a date range of no more than 365 days." : "";
});
const rangeValid = computed(() => !rangeError.value);

const filteredLocations = computed(() => {
    const query = locationSearch.value.toLowerCase();
    const locations = stats.value?.locations || [];
    if (!query) return locations;
    return locations.filter((location) =>
        `${location.organization_name} ${location.location_name}`.toLowerCase().includes(query)
    );
});

const dailyBars = computed(() => {
    const rows = stats.value?.daily || [];
    const maxRuns = Math.max(1, ...rows.map((row) => Number(row.completed_runs) || 0));
    return rows.map((row, index) => {
        const date = new Date(`${row.date}T00:00:00`);
        return {
            ...row,
            height: Math.max(5, (Number(row.completed_runs) / maxRuns) * 100),
            dateLabel: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
            showLabel: index === 0 || index === rows.length - 1 || index % 5 === 0,
        };
    });
});

async function load() {
    if (!rangeValid.value) return;
    loading.value = true;
    errorMessage.value = "";
    try {
        const response = await api.get("/admin/auto-engine-insights", {
            params: { from_date: fromDate.value, to_date: toDate.value },
        });
        if (!response.data?.data) {
            throw new Error("The auto-engine insights response was empty.");
        }
        stats.value = response.data.data;
    } catch (error) {
        console.error("Error fetching global auto-engine insights:", error);
        errorMessage.value = error?.response?.data?.message
            || error.message
            || "Auto-Engine insights could not be loaded.";
    } finally {
        loading.value = false;
    }
}

function formatNumber(value) {
    return new Intl.NumberFormat().format(Number(value) || 0);
}

function formatDuration(value) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
    const seconds = Math.max(0, Math.round(Number(value)));
    if (seconds < 60) return `${seconds}s`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
}

function formatTimestamp(value) {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function formatExecutionType(value) {
    return ({
        autoschedule: "Auto schedule",
        autosubstitute: "Auto substitute",
        userinitiated: "User initiated",
    })[value] || value || "Other";
}

function completionPercent(type) {
    const total = Number(type.total_runs) || 0;
    return total ? Math.min(100, (Number(type.completed_runs) / total) * 100) : 0;
}

watch(() => props.modelValue, (isOpen) => {
    if (isOpen) load();
});
</script>

<style scoped>
.engine-insights {
    color: #1e293b;
}

.insights-hero {
    overflow: hidden;
    margin: -4px 0 20px;
    padding: 22px 24px;
    border-radius: 18px;
    color: #fff;
    background:
        radial-gradient(circle at 90% 0%, rgba(134, 239, 172, 0.25), transparent 32%),
        linear-gradient(120deg, #102b46, #164e63 58%, #166534);
}

.insights-kicker {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 11px;
    color: #bbf7d0;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.15em;
}

.insights-kicker .material-icons {
    font-size: 17px;
}

.insights-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.insights-heading h2 {
    margin: 0 0 5px;
    color: #fff;
    font-size: 1.45rem;
    font-weight: 750;
}

.insights-heading p {
    margin: 0;
    color: rgba(255, 255, 255, 0.78);
    font-size: 0.88rem;
}

.scope-pill {
    flex: 0 0 auto;
    padding: 6px 10px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 999px;
    color: #ecfdf5;
    background: rgba(255, 255, 255, 0.1);
    font-size: 0.72rem;
    font-weight: 700;
}

.range-controls {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 18px;
}

.range-controls label {
    display: grid;
    gap: 4px;
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 700;
}

.range-controls input {
    min-width: 150px;
}

.updated-label {
    margin-left: auto;
    padding-bottom: 7px;
    color: #64748b;
    font-size: 0.72rem;
}

.metric-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}

.metric {
    min-height: 120px;
    padding: 15px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 15px;
    background: #fff;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
}

.metric-label,
.metric small {
    display: block;
    color: #64748b;
    font-size: 0.72rem;
}

.metric strong {
    display: block;
    margin: 7px 0 4px;
    color: #0f172a;
    font-size: clamp(1.45rem, 3vw, 2rem);
    font-weight: 800;
    line-height: 1.1;
}

.metric--green { border-top: 3px solid #22c55e; }
.metric--blue { border-top: 3px solid #0ea5e9; }
.metric--violet { border-top: 3px solid #8b5cf6; }
.metric--amber { border-top: 3px solid #f59e0b; }

.visualization-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    gap: 14px;
    margin-bottom: 14px;
}

.insight-panel {
    min-width: 0;
    padding: 18px;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: #fff;
}

.data-panel {
    margin-bottom: 14px;
}

.panel-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 15px;
}

.panel-heading h3 {
    margin: 0 0 4px;
    color: #0f172a;
    font-size: 0.98rem;
    font-weight: 750;
}

.panel-heading p {
    margin: 0;
    color: #64748b;
    font-size: 0.75rem;
}

.panel-icon {
    display: grid;
    width: 34px;
    height: 34px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 11px;
}

.panel-icon .material-icons { font-size: 19px; }
.panel-icon--blue { color: #0369a1; background: #e0f2fe; }
.panel-icon--green { color: #15803d; background: #dcfce7; }

.daily-chart {
    display: flex;
    align-items: stretch;
    gap: 5px;
    min-height: 170px;
    overflow-x: auto;
    padding: 8px 2px 0;
}

.daily-column {
    display: flex;
    min-width: 27px;
    flex: 1 0 27px;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    color: #64748b;
    font-size: 0.62rem;
}

.daily-value {
    min-height: 14px;
    color: #475569;
    font-size: 0.62rem;
}

.daily-track {
    display: flex;
    width: 100%;
    height: 126px;
    align-items: flex-end;
    justify-content: center;
}

.daily-bar {
    width: min(20px, 78%);
    min-height: 5px;
    border-radius: 6px 6px 2px 2px;
    background: linear-gradient(180deg, #38bdf8, #0284c7);
    transition: height 180ms ease;
}

.daily-column small {
    min-height: 17px;
    margin-top: 5px;
    white-space: nowrap;
}

.type-breakdown {
    display: grid;
    gap: 17px;
    padding-top: 3px;
}

.type-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 7px 12px;
    align-items: center;
}

.type-copy strong,
.type-copy span {
    display: block;
}

.type-copy strong {
    color: #1e293b;
    font-size: 0.8rem;
}

.type-copy span,
.type-row small {
    color: #64748b;
    font-size: 0.68rem;
}

.type-track {
    grid-column: 1 / -1;
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: #e2e8f0;
}

.type-bar {
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #34d399, #059669);
}

.result-count {
    flex: 0 0 auto;
    padding: 5px 9px;
    border-radius: 999px;
    color: #475569;
    background: #f1f5f9;
    font-size: 0.68rem;
    font-weight: 700;
}

.insights-table-wrap {
    max-height: 320px;
    overflow: auto;
}

.location-table-wrap {
    max-height: 400px;
}

.insights-table {
    min-width: 800px;
    font-size: 0.75rem;
}

.insights-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    color: #64748b;
    background: #f8fafc;
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
}

.insights-table td {
    color: #475569;
    white-space: nowrap;
}

.entity-name {
    color: #0f172a !important;
    font-weight: 700;
}

.completion-count {
    display: inline-block;
    min-width: 28px;
    padding: 3px 7px;
    border-radius: 999px;
    color: #166534;
    background: #dcfce7;
    font-weight: 800;
    text-align: center;
}

.location-heading {
    align-items: center;
}

.location-search {
    max-width: 280px;
}

.empty-note {
    margin: 20px 0 4px;
    color: #64748b;
    font-size: 0.8rem;
    text-align: center;
}

.data-note {
    padding: 12px 14px;
    border-radius: 10px;
    color: #475569;
    background: #f1f5f9;
    font-size: 0.72rem;
    line-height: 1.5;
}

.loading-state {
    display: flex;
    min-height: 260px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    color: #475569;
    font-size: 0.88rem;
}

@media (max-width: 900px) {
    .metric-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .visualization-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 540px) {
    .insights-heading,
    .location-heading {
        align-items: flex-start;
        flex-direction: column;
    }

    .scope-pill,
    .location-search {
        max-width: 100%;
    }

    .metric-grid {
        gap: 8px;
    }

    .metric {
        min-height: 108px;
        padding: 12px;
    }

    .updated-label {
        width: 100%;
        margin-left: 0;
    }
}
</style>
